import type { ActionListItem } from '@latitude/action-list';
import { ActionList } from '@latitude/action-list';
import { Icon } from '@latitude/icons';
import { Tooltip } from '@latitude/tooltip';
import { useCallback, useEffect, useState } from 'react';
import type {
  ToggleGroupSelectItemComponentProps,
  ToggleGroupSelectedValue,
} from 'types';
import { useDeepCompareCallback, useDeepCompareMemo } from 'use-deep-compare';

export const ToggleGroupItemSelect = <T,>({
  selected,
  componentTriggerType,
  item,
  analytics,
  changeItemSelectToSelected,
  Component,
  ...rest
}: ToggleGroupSelectItemComponentProps<T>) => {
  const [open, setOpen] = useState<boolean>(false);
  const [optionsToShow, setOptionsToShow] = useState<ActionListItem<T>[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>();

  // Since the item can have nested options, we need to handle it as ToggleGroupItemSelect
  const {
    id,
    label,
    options,
    onClick,
    onRemove,
    tooltipLabelFormatter = (value) => value.label,
    selectedValueFormatter = (value) => value.value,
    isMulti,
    defaultTooltip,
    withSearch = false,
  } = item;

  const toolipLabel = useDeepCompareMemo(
    () => (selected ? tooltipLabelFormatter(selected) : (defaultTooltip ?? '')),
    [selected, tooltipLabelFormatter, defaultTooltip],
  );

  const nestedOptionsMap: Record<string, ActionListItem<T>[]> =
    useDeepCompareMemo(
      () =>
        options.reduce((acc, option) => {
          if (option.nestedOptions) {
            return {
              ...acc,
              [option.value]: option.nestedOptions,
            };
          }

          return acc;
        }, {}),
      [options],
    );

  const selectedLabelMap: Record<string, string> = useDeepCompareMemo(
    () =>
      options.reduce((acc, option) => {
        return {
          ...acc,
          [option.value]: option.selectedLabel ?? option.label,
        };
      }, {}),
    [options],
  );

  // Only want this to trigger on render
  useEffect(() => {
    const foundNestedOptions = Object.entries(nestedOptionsMap).find(
      ([_key, nestedOptions]) => {
        const foundOption = nestedOptions.find(
          (opt) => opt.value === selected?.value,
        );
        return Boolean(foundOption);
      },
    );

    setOptionsToShow(foundNestedOptions ? foundNestedOptions[1] : options);
  }, [nestedOptionsMap, options, selected?.value]);

  useEffect(() => {
    if (!selected) {
      setOptionsToShow(options);
    }
  }, [selected, options]);

  const onChange = useCallback(
    (
      selectedValue: string | string[],
      fullSelectedValue: ActionListItem<T>,
    ) => {
      // Handle nested options navigation (only for single select)
      if (
        !isMulti &&
        typeof selectedValue === 'string' &&
        nestedOptionsMap[selectedValue]
      ) {
        setOptionsToShow(nestedOptionsMap[selectedValue] ?? []);
        return;
      }

      const newItemBase: ToggleGroupSelectedValue<T> = {
        ...fullSelectedValue,
        id,
        value: selectedValue,
      };

      const newItem: ToggleGroupSelectedValue<T> = {
        ...fullSelectedValue,
        id,
        value: selectedValueFormatter(newItemBase),
      };

      if (isMulti && Array.isArray(selectedValue)) {
        setSelectedLabel(
          selectedValue.length > 0 ? `${selectedValue.length} selected` : label,
        );
      } else {
        setSelectedLabel(
          selectedLabelMap[selectedValue as string]
            ? selectedLabelMap[selectedValue as string]
            : fullSelectedValue.label,
        );
      }

      analytics('toggle_selected_item', { ...newItem, type: 'select' });
      onClick?.(newItem);

      // Only close dropdown for single select
      if (!isMulti) {
        setOpen(false);
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [onClick, isMulti],
  );

  const triggerItemLabel =
    changeItemSelectToSelected && selected ? selectedLabel : label;

  const handleSetOpen = useDeepCompareCallback(
    (isOpen: boolean) => {
      // When navigating to nested options when you already have an option selected
      // We want to check if the value is in the current options, if it isn't it means its in the one above
      // and we need to reset the options back to the original one.
      const foundOption = optionsToShow.find(
        (option) => option.value === selected?.value,
      );

      setOpen(isOpen);

      if (!isOpen && !foundOption) {
        setTimeout(() => {
          setOptionsToShow(options);
        }, 200);
      }
    },
    [options, optionsToShow, selected?.value],
  );

  // Wrapping the component in a Tooltip if selected and a value is present
  return (
    <Tooltip
      key={id}
      text={toolipLabel}
      hideTooltip={!selected && !defaultTooltip}
    >
      <ActionList
        isMulti={isMulti}
        itemSize="small"
        closeOnOptionClick={false}
        setOpen={handleSetOpen}
        withSearch={withSearch}
        // oxlint-disable-next-line typescript/no-explicit-any -- Pre-existing usage; CEXP-20389
        onChange={(value: any, selectedValue: any) =>
          onChange(value, selectedValue)
        }
        options={optionsToShow}
        open={open}
        popoverContentProps={{ side: 'bottom', align: 'start' }}
        value={
          isMulti
            ? ((selected?.value as string[]) ?? [])
            : ((selected?.value as string) ?? '')
        }
      >
        <Component
          {...rest}
          aria-pressed={!!selected}
          selected={!!selected}
          {...(componentTriggerType === 'button'
            ? {
                trailingElement: (
                  <Icon
                    name="chevron-left"
                    direction={open ? 'up' : 'down'}
                    aria-hidden="true"
                  />
                ),
              }
            : {})}
          onRemove={onRemove}
        >
          {triggerItemLabel}{' '}
          {componentTriggerType === 'tag' && (
            <Icon
              name="chevron-left"
              direction={open ? 'up' : 'down'}
              aria-hidden="true"
            />
          )}
        </Component>
      </ActionList>
    </Tooltip>
  );
};
